import { useState, useEffect } from "react";
import { getLessonPlanFilterOptions } from "../../../services/LessonPlanService";
import { getUsers } from "../../../services/UserService";
import { getPrograms } from "../../../services/ProgramServices";
import { getClassWiseSubjects } from "../../../services/SubjectServices";
import { toOptions } from "../utils/lessonPlanUtils";

/**
 * Teacher, class and subject options for the All Lesson Plans filter, from
 * GET lesson-plan/filter-options. Falls back to the general user / class /
 * subject APIs for any list that endpoint does not return.
 */
export const useLessonPlanFilterOptions = (programId) => {
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);

  useEffect(() => {
    let isCurrent = true;

    const fetchOptions = async () => {
      let options = {};
      try {
        const response = await getLessonPlanFilterOptions();
        options = response?.data?.data || {};
      } catch (error) {
        console.error("lesson-plan/filter-options failed, using fallbacks:", error);
      }

      let teachers = toOptions(options.teachers);
      let programs = toOptions(options.programs ?? options.classes);

      const [usersResult, programsResult] = await Promise.allSettled([
        teachers.length ? null : getUsers({ page: 1, pageSize: 500 }),
        programs.length ? null : getPrograms(),
      ]);
      if (!teachers.length && usersResult.value?.success) teachers = toOptions(usersResult.value.data);
      if (!programs.length && programsResult.value?.success) programs = toOptions(programsResult.value.data);

      if (isCurrent) {
        setTeacherOptions(teachers);
        setProgramOptions(programs);
      }
    };

    fetchOptions();
    return () => {
      isCurrent = false;
    };
  }, []);

  // Subjects depend on the selected class
  useEffect(() => {
    if (!programId) {
      setSubjectOptions([]);
      return undefined;
    }

    let isCurrent = true;
    getLessonPlanFilterOptions({ program_id: programId })
      .then((response) => toOptions(response?.data?.data?.subjects))
      .catch(() => [])
      .then(async (subjects) => {
        if (subjects.length) return subjects;
        const response = await getClassWiseSubjects(programId);
        return toOptions(response.data.data);
      })
      .then((subjects) => isCurrent && setSubjectOptions(subjects))
      .catch(() => isCurrent && setSubjectOptions([]));

    return () => {
      isCurrent = false;
    };
  }, [programId]);

  return { teacherOptions, programOptions, subjectOptions };
};
